import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar, Play } from "lucide-react";

// Local-TZ-safe ISO string — avoids the IST off-by-one bug that plagued PDFs.
const _iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const today = () => _iso(new Date());

const firstOfThisMonth = () => {
  const d = new Date();
  return _iso(new Date(d.getFullYear(), d.getMonth(), 1));
};

const firstOfLastMonth = () => {
  const d = new Date();
  return _iso(new Date(d.getFullYear(), d.getMonth() - 1, 1));
};

const lastOfLastMonth = () => {
  const d = new Date();
  return _iso(new Date(d.getFullYear(), d.getMonth(), 0));
};

// Indian FY: Apr 1 → Mar 31
const fyRange = () => {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return { start: _iso(new Date(y, 3, 1)), end: _iso(new Date(y + 1, 2, 31)) };
};

const PRESETS = [
  { label: "Today", get: () => ({ start: today(), end: today() }) },
  { label: "This month", get: () => ({ start: firstOfThisMonth(), end: today() }) },
  { label: "Last month", get: () => ({ start: firstOfLastMonth(), end: lastOfLastMonth() }) },
  { label: "This FY", get: fyRange },
  { label: "All time", get: () => ({ start: "", end: "" }) },
];

/**
 * Date-range picker with an explicit "Go" button.
 *
 * Props:
 *   start, end — live (user-typing) values
 *   onChange({start,end}) — updates the live values (no fetch)
 *   onApply()  — commits the current values (fetch). When provided, a green
 *                "Go" button appears between the date inputs and the presets.
 *                Pressing a preset also auto-applies (it both updates AND
 *                commits — the user clearly expects an immediate result).
 *   label      — label shown next to the calendar icon (default "Period")
 *   dirty      — bool, set true when start/end differ from the applied values
 *                — pulses the Go button to nudge the user to commit.
 */
export default function DateRangePicker({ start, end, onChange, onApply, label = "Period", dirty = false }) {
  const handlePreset = (preset) => {
    const next = preset.get();
    onChange(next);
    if (onApply) onApply(next);
  };

  return (
    <div className="flex flex-wrap items-end gap-2 no-print">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-gray-500 ml-1" />
        <span className="text-xs uppercase tracking-wider text-gray-600 mr-1">{label}</span>
      </div>
      <Input
        type="date"
        value={start}
        onChange={(e) => onChange({ start: e.target.value, end })}
        className="w-40 h-8"
        data-testid="input-range-start"
        placeholder="From"
      />
      <span className="text-gray-400 text-sm">→</span>
      <Input
        type="date"
        value={end}
        onChange={(e) => onChange({ start, end: e.target.value })}
        className="w-40 h-8"
        data-testid="input-range-end"
        placeholder="To"
      />
      {onApply && (
        <Button
          variant="default"
          size="sm"
          onClick={() => onApply()}
          className={`h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold ${dirty ? "animate-pulse ring-2 ring-emerald-300" : ""}`}
          data-testid="btn-period-go"
          title="Apply this date range and refresh the report"
        >
          <Play className="w-3.5 h-3.5 mr-1 fill-current" /> Go
        </Button>
      )}
      <div className="flex flex-wrap items-center gap-1 ml-1">
        {PRESETS.map((p) => (
          <Button
            key={p.label}
            variant="outline"
            size="sm"
            onClick={() => handlePreset(p)}
            className="h-8 text-xs"
            data-testid={`btn-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {p.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
